<template>
  <div class="vvou">
    <APageHead
      title="바우처 · 등록 내역"
      sub="전 기관의 클래스 지정과 학생 등록 내역, 기관별 통계를 봅니다. 학생 문의는 등록 코드(결제코드)로 찾습니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="loadCurrent">새로고침</ABtn>
      </template>
    </APageHead>

    <div class="p-tabs">
      <button
        v-for="t in TABS"
        :key="t.id"
        type="button"
        :class="['p-tabs__tab', { 'is-active': tab === t.id }]"
        @click="switchTab(t.id)"
      >
        <i class="material-icons-round vvou__tab-icon">{{ t.icon }}</i>
        {{ t.label }}
        <span v-if="state[t.id].loaded && t.id !== 'stats'" class="p-tabs__count">{{ state[t.id].total }}</span>
      </button>
    </div>

    <ACard overflow="visible">
      <div class="vvou__filters">
        <div class="p-field">
          <label class="p-field__label">기관</label>
          <select v-model="cur.filters.organization_idx" class="p-input" @change="search">
            <option value="">전체 기관</option>
            <option v-for="o in orgOptions" :key="o.idx" :value="o.idx">{{ o.name }}</option>
          </select>
        </div>

        <template v-if="tab === 'vouchers'">
          <div class="p-field">
            <label class="p-field__label">등록 코드</label>
            <input v-model.trim="cur.filters.code" class="p-input vvou__mono" type="text" placeholder="결제코드 (pay_code)" @keyup.enter="search" />
          </div>
          <div class="p-field">
            <label class="p-field__label">검색어</label>
            <input v-model.trim="cur.filters.q" class="p-input" type="text" placeholder="학생 이름 · 이메일, 교재명" @keyup.enter="search" />
          </div>
          <div class="p-field">
            <label class="p-field__label">상태</label>
            <select v-model="cur.filters.status" class="p-input" @change="search">
              <option value="">전체</option>
              <option v-for="(v, k) in VOUCHER_STATUS" :key="k" :value="k">{{ v.label }}</option>
            </select>
          </div>
        </template>

        <div v-else-if="tab === 'classVouchers'" class="p-field">
          <label class="p-field__label">상태</label>
          <select v-model="cur.filters.status" class="p-input" @change="search">
            <option value="">전체</option>
            <option v-for="(v, k) in CLASS_VOUCHER_STATUS" :key="k" :value="k">{{ v.label }}</option>
          </select>
        </div>

        <template v-else>
          <div class="p-field">
            <label class="p-field__label">시작일 (KST)</label>
            <input v-model="cur.filters.date_from" class="p-input" type="date" />
          </div>
          <div class="p-field">
            <label class="p-field__label">종료일 (KST)</label>
            <input v-model="cur.filters.date_to" class="p-input" type="date" />
          </div>
        </template>

        <div class="vvou__filter-actions">
          <ABtn kind="primary" icon="search" @click="search">조회</ABtn>
          <ABtn kind="ghost" icon="refresh" @click="resetFilters">초기화</ABtn>
        </div>
      </div>
    </ACard>

    <!-- 처리 중·실패 건은 복구 배치가 이어 처리합니다. 오래 남아 있으면 배치 로그를 봐야 합니다. -->
    <div v-if="tab === 'vouchers' && recoveryRows.length" class="vvou__banner">
      <i class="material-icons-round">sync_problem</i>
      이 페이지에 처리 대기 · 실패 건이 {{ recoveryRows.length }}건 있습니다. 복구 배치가 이어서 처리합니다 — 오래 남아 있으면
      <router-link :to="{ name: 'SystemLogsSchedules' }">로그 · 스케쥴</router-link> 에서 복구 배치 기록을 확인해 주세요.
    </div>

    <ACard>
      <div v-if="cur.loading" class="vvou__loading"><div class="p-spinner p-spinner--lg"></div></div>
      <LoadError v-else-if="cur.error" :message="cur.error" @retry="loadCurrent" />
      <AEmpty v-else-if="!cur.items.length" title="조회 결과가 없습니다" />

      <template v-else>
        <!-- 등록 내역 -->
        <ATable v-if="tab === 'vouchers'" :columns="voucherCols" :rows="cur.items" row-key="voucher_idx">
          <template #cell:at="{ row }">
            <div class="vvou__stack">
              <span class="vvou__mono vvou__small">{{ fmtKst(row.registered_at || row.created_at) }}</span>
              <span v-if="row.status === 'FAILED'" class="vvou__sub vvou__mono">실패 {{ fmtKst(row.failed_at) }}</span>
            </div>
          </template>
          <template #cell:code="{ row }">
            <button type="button" class="vvou__code" title="복사" @click="copy(row.code)">
              {{ row.code || '-' }}
            </button>
          </template>
          <template #cell:student="{ row }">
            <div class="vvou__stack">
              <span class="vvou__strong">{{ row.student_name || '-' }}</span>
              <span class="vvou__sub">{{ row.student_email || '' }}</span>
            </div>
          </template>
          <template #cell:where="{ row }">
            <div class="vvou__stack">
              <span>{{ row.organization_name || '-' }}</span>
              <!-- 삭제된 클래스면 classroom_name 이 null 입니다(가이드 §2 행 필드). -->
              <span class="vvou__sub">{{ row.classroom_name || (row.classroom_idx ? `삭제된 클래스 #${row.classroom_idx}` : '') }}</span>
            </div>
          </template>
          <template #cell:book="{ row }">
            <div class="vvou__stack">
              <span :title="bookTitle(row)">{{ bookTitle(row) }}</span>
              <span><APill v-if="gradeLabel(row.grade)" size="sm" :kind="gradePillKind(row.grade)">{{ gradeLabel(row.grade) }}</APill></span>
            </div>
          </template>
          <template #cell:amount="{ row }">
            <div class="vvou__stack vvou__stack--end">
              <b class="vvou__mono">{{ money(row.amount, row.currency_code) }}</b>
              <span v-if="row.list_price != null" class="vvou__sub vvou__mono">
                정가 {{ money(row.list_price, row.currency_code) }}{{ Number(row.discount_rate) > 0 ? ` · 할인 ${percent(row.discount_rate)}` : '' }}
              </span>
            </div>
          </template>
          <template #cell:status="{ row }">
            <div class="vvou__stack vvou__stack--center">
              <APill :kind="voucherStatusKind(row.status)" dot>{{ voucherStatusLabel(row.status) }}</APill>
              <span v-if="row.fail_reason" class="vvou__sub" :title="row.fail_reason">{{ row.fail_reason }}</span>
            </div>
          </template>
        </ATable>

        <!-- 지정 목록 -->
        <ATable v-else-if="tab === 'classVouchers'" :columns="classVoucherCols" :rows="cur.items" row-key="idx">
          <template #cell:where="{ row }">
            <div class="vvou__stack">
              <span class="vvou__strong">{{ row.organization_name || row.organization?.name || '-' }}</span>
              <span class="vvou__sub">{{ row.classroom_name || row.classroom?.name || '' }}</span>
            </div>
          </template>
          <template #cell:book="{ row }">
            <div class="vvou__stack">
              <span>{{ bookTitle(row) }}</span>
              <span><APill v-if="gradeLabel(row.grade)" size="sm" :kind="gradePillKind(row.grade)">{{ gradeLabel(row.grade) }}</APill></span>
            </div>
          </template>
          <template #cell:regs="{ row }">
            <span class="vvou__mono">{{ row.registered_count ?? 0 }}{{ row.max_registrations ? ` / ${row.max_registrations}` : '' }}</span>
          </template>
          <template #cell:until="{ row }"><span class="vvou__mono vvou__small">{{ row.register_until || '-' }}</span></template>
          <template #cell:by="{ row }">
            <div class="vvou__stack">
              <span>{{ row.created_by?.name || row.assigned_by?.name || '-' }}</span>
              <span class="vvou__sub vvou__mono">{{ fmtKst(row.created_at) }}</span>
            </div>
          </template>
          <template #cell:status="{ row }">
            <APill :kind="classVoucherStatusKind(row.status)" dot>{{ classVoucherStatusLabel(row.status) }}</APill>
          </template>
        </ATable>

        <!-- 기관별 통계 -->
        <ATable v-else :columns="statCols" :rows="cur.items" row-key="idx">
          <template #cell:org="{ row }">
            <div class="vvou__stack">
              <router-link class="vvou__link" :to="{ name: 'VoucherOrganizationDetail', params: { organizationIdx: row.idx }, query: { tab: 'balance' } }">
                {{ row.name || `#${row.idx}` }}
              </router-link>
              <span class="vvou__sub vvou__mono">{{ row.voucher_currency }}{{ row.active_yn === 'N' ? ' · 비활성' : '' }}</span>
            </div>
          </template>
          <template #cell:in="{ row }">
            <div class="vvou__stack vvou__stack--end">
              <span class="vvou__mono">{{ money(row.purchased_total, row.voucher_currency) }}</span>
              <span v-if="isPositive(row.voided_total)" class="vvou__mono vvou__sub">취소 {{ money(row.voided_total, row.voucher_currency) }}</span>
            </div>
          </template>
          <template #cell:out="{ row }">
            <div class="vvou__stack vvou__stack--end">
              <span class="vvou__mono">반환 {{ money(row.withdrawn_total, row.voucher_currency) }}</span>
              <span class="vvou__mono vvou__sub">정정 {{ money(row.adjusted_total, row.voucher_currency) }}</span>
            </div>
          </template>
          <template #cell:used="{ row }">
            <div class="vvou__stack vvou__stack--end">
              <span class="vvou__mono">{{ money(row.used_amount, row.voucher_currency) }}</span>
              <span class="vvou__mono vvou__sub">기간 {{ row.period_registered_count ?? 0 }}건 · {{ money(row.period_registered_amount, row.voucher_currency) }}</span>
            </div>
          </template>
          <template #cell:pending="{ row }"><span class="vvou__mono">{{ money(row.pending_amount, row.voucher_currency) }}</span></template>
          <template #cell:balance="{ row }"><b class="vvou__mono">{{ money(row.voucher_balance, row.voucher_currency) }}</b></template>
          <template #cell:check="{ row }">
            <APill v-if="row.identity_consistent === false || row.ledger_consistent === false" kind="err">불일치</APill>
            <APill v-else-if="row.identity_consistent === true" kind="ok">일치</APill>
            <span v-else class="vvou__dash">—</span>
          </template>
        </ATable>

        <APagination
          v-if="tab !== 'stats'"
          :current-page="cur.page"
          :total="cur.total"
          :page-size="cur.size"
          @change="onPage"
        />
      </template>
    </ACard>
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import LoadError from "@/views/voucher/components/LoadError.vue";
import { gradeLabel, gradePillKind } from "@/utils/paymentDisplay";
import {
  fmtKst, money, percent, isPositive, pageOf, bodyOf, loadErrorText,
  VOUCHER_STATUS, voucherStatusLabel, voucherStatusKind, needsRecoveryCheck,
  CLASS_VOUCHER_STATUS, classVoucherStatusLabel, classVoucherStatusKind,
} from "@/utils/voucher";

const TABS = [
  { id: "vouchers", label: "학생 등록 내역", icon: "how_to_reg", url: "/voucher/admin/vouchers" },
  { id: "classVouchers", label: "클래스 지정", icon: "assignment", url: "/voucher/admin/class-vouchers" },
  { id: "stats", label: "기관별 통계", icon: "bar_chart", url: "/voucher/admin/stats" },
];

const blankFilters = () => ({ organization_idx: "", status: "", code: "", q: "", date_from: "", date_to: "" });
const blankState = () => ({ items: [], total: 0, page: 1, size: 20, loading: false, loaded: false, error: "", filters: blankFilters() });

export default {
  name: "VoucherVouchers",
  components: { APageHead, ACard, ABtn, APill, ATable, AEmpty, APagination, LoadError },
  data() {
    return {
      TABS,
      VOUCHER_STATUS,
      CLASS_VOUCHER_STATUS,
      tab: "vouchers",
      orgOptions: [],
      state: { vouchers: blankState(), classVouchers: blankState(), stats: blankState() },
      voucherCols: [
        { key: "at", label: "등록 (KST)", width: 140 },
        { key: "code", label: "등록 코드", width: 170 },
        { key: "student", label: "학생", width: 190 },
        { key: "where", label: "기관 · 클래스", width: 200 },
        { key: "book", label: "도서 · 등급", wrap: true },
        { key: "amount", label: "차감액", width: 140, align: "right" },
        { key: "status", label: "상태", width: 110, align: "center" },
      ],
      classVoucherCols: [
        { key: "where", label: "기관 · 클래스", width: 220 },
        { key: "book", label: "도서 · 등급", wrap: true },
        { key: "regs", label: "등록 / 상한", width: 110, align: "center" },
        { key: "until", label: "등록 기한", width: 110 },
        { key: "by", label: "지정", width: 160 },
        { key: "status", label: "상태", width: 100, align: "center" },
      ],
      statCols: [
        { key: "org", label: "기관", width: 220 },
        { key: "in", label: "확정 구매", width: 140, align: "right" },
        { key: "out", label: "반환 · 정정", width: 160, align: "right" },
        { key: "used", label: "사용액 (누계 · 기간)", width: 180, align: "right" },
        { key: "pending", label: "처리 중", width: 130, align: "right" },
        { key: "balance", label: "잔액", width: 140, align: "right" },
        { key: "check", label: "항등식", width: 90, align: "center" },
      ],
    };
  },
  computed: {
    cur() {
      return this.state[this.tab];
    },
    recoveryRows() {
      return this.state.vouchers.items.filter((r) => needsRecoveryCheck(r.status));
    },
  },
  created() {
    const q = this.$route.query;
    if (q.code) this.state.vouchers.filters.code = String(q.code);
    if (q.organization_idx) {
      Object.values(this.state).forEach((s) => { s.filters.organization_idx = String(q.organization_idx); });
    }
    this.loadOrgOptions();
    this.loadCurrent();
  },
  methods: {
    fmtKst, money, percent, isPositive, gradeLabel, gradePillKind,
    voucherStatusLabel, voucherStatusKind, classVoucherStatusLabel, classVoucherStatusKind,
    bookTitle(row) {
      return row.textbook?.title || row.title || row.barcode || "-";
    },
    async copy(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.$notify?.({ type: "success", text: "등록 코드를 복사했습니다." });
      } catch (e) {
        console.error("복사 실패:", e);
      }
    },
    async loadOrgOptions() {
      try {
        const res = await this.$axios({ method: "get", url: "/voucher/admin/organizations", params: { page: 1, size: 500 }, loadingBar: false });
        this.orgOptions = pageOf(res).items.map((o) => ({ idx: String(o.idx ?? o.organization_idx), name: o.name }));
      } catch (e) {
        console.error("기관 목록 조회 실패:", e);
      }
    },
    switchTab(id) {
      if (this.tab === id) return;
      this.tab = id;
      if (!this.cur.loaded) this.loadCurrent();
    },
    search() {
      this.cur.page = 1;
      this.loadCurrent();
    },
    resetFilters() {
      const org = this.cur.filters.organization_idx;
      this.cur.filters = { ...blankFilters(), organization_idx: org };
      this.search();
    },
    onPage(p) {
      this.cur.page = p;
      this.loadCurrent();
    },
    buildParams() {
      const f = this.cur.filters;
      const params = {};
      if (f.organization_idx) params.organization_idx = f.organization_idx;
      if (this.tab === "stats") {
        if (f.date_from) params.date_from = f.date_from;
        if (f.date_to) params.date_to = f.date_to;
        return params;
      }
      params.page = this.cur.page;
      params.size = this.cur.size;
      if (f.status) params.status = f.status;
      if (this.tab === "vouchers") {
        if (f.code) params.code = f.code;
        if (f.q) params.q = f.q;
      }
      return params;
    },
    async loadCurrent() {
      const id = this.tab;
      const S = this.state[id];
      if (S.filters.date_from && S.filters.date_to && S.filters.date_from > S.filters.date_to) {
        swal.fire("확인해 주세요", "시작일이 종료일보다 늦습니다.", "warning");
        return;
      }
      S.loading = true;
      S.error = "";
      try {
        const res = await this.$axios({ method: "get", url: TABS.find((t) => t.id === id).url, params: this.buildParams(), loadingBar: true });
        if (id === "stats") {
          // 통계는 {date_from, date_to, items[]} 이고 행 = 기관 + 잔액 요약 + 기간 집계입니다(가이드 §3-4).
          const rows = bodyOf(res)?.items || [];
          S.items = rows;
          S.total = rows.length;
        } else {
          const p = pageOf(res, S.page);
          S.items = p.items;
          S.total = p.total;
        }
        S.loaded = true;
      } catch (e) {
        console.error("바우처 내역 조회 실패:", e);
        S.error = loadErrorText(e);
        S.items = [];
        S.total = 0;
      } finally {
        S.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.vvou {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__tab-icon { font-size: 15px !important; }

  &__filters {
    display: grid;
    grid-template-columns: repeat(4, 1fr) auto;
    gap: 12px;
    align-items: end;

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__filter-actions { display: flex; gap: 6px; }

  &__banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    background: $p-warn-soft;
    color: $p-warn-ink;
    border: 1px solid $p-warn;
    font-size: 13px;

    i { font-size: 18px; }
    a { font-weight: 700; color: inherit; text-decoration: underline; }
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 48px;
  }

  &__stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    &--end { align-items: flex-end; }
    &--center { align-items: center; }
  }

  &__strong { font-weight: 700; color: $p-ink; }
  &__sub { font-size: 11px; color: $p-ink-soft; }
  &__small { font-size: 11px; }
  &__dash { color: $p-ink-softer; }
  &__link { color: $p-info-ink; font-weight: 700; }

  &__mono {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
  }

  &__code {
    padding: 2px 6px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-sm;
    background: $p-panel-alt;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink;
    cursor: copy;
  }
}
</style>
